import { useState } from "react";
import { getOptimizedImage } from "@/lib/optimized-images";
import { sanitizeCoastalAltText } from "@/lib/alt-text";

type Props = {
  src?: string | null;
  alt: string;
  /** Gradient shown underneath / as fallback when there is no image. */
  fallback?: string;
  className?: string;
  imgClassName?: string;
  objectPosition?: string;
  width?: number;
  height?: number;
  eager?: boolean;
  sizes?: string;
  /**
   * cover  - always fill and crop (photographs)
   * contain- always show the whole image over a blurred copy of itself
   * auto   - cover when the image roughly matches the frame, contain when it
   *          does not (posters/creatives get shown whole instead of sliced)
   */
  fit?: "cover" | "contain" | "auto";
  /** Children render on top of the image (badges, scrims, titles). */
  children?: React.ReactNode;
};

/**
 * One image primitive for the whole site.
 * - automatic high-fidelity AVIF and WebP delivery via local asset registry
 * - strict coastal community alt text sanitization
 * - gradient placeholder underneath, so a slow or missing image never shows a white hole
 * - lazy + async decoding with eager high-priority support for LCP
 * - explicit width/height to eliminate layout shift
 */
export function SmartImage({
  src,
  alt,
  fallback = "var(--gradient-ocean)",
  className = "",
  imgClassName = "",
  objectPosition = "center",
  width = 1600,
  height = 1200,
  eager = false,
  sizes,
  fit = "cover",
  children,
}: Props) {
  const [failed, setFailed] = useState(false);
  const [contain, setContain] = useState(fit === "contain");
  const show = !!src && !failed;
  // Respect a positioning class passed in (e.g. "absolute inset-0") instead of forcing relative.
  const positioned = /(^|\s)(absolute|fixed|sticky|relative)(\s|$)/.test(className);
  const cleanAlt = sanitizeCoastalAltText(alt);
  const optimized = getOptimizedImage(src ?? undefined);

  function handleLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (fit !== "auto") return;
    const img = e.currentTarget;
    const box = img.parentElement?.parentElement || img.parentElement;
    if (!img.naturalWidth || !img.naturalHeight || !box) return;
    const imgAR = img.naturalWidth / img.naturalHeight;
    const boxAR = (box.clientWidth || 1) / (box.clientHeight || 1);
    // More than ~30% off the frame means cover would slice something important.
    const off = Math.abs(Math.log(imgAR / boxAR));
    setContain(off > 0.3);
  }

  const resolvedSrc = optimized?.fallback || src!;

  return (
    <div
      className={`${positioned ? "" : "relative"} overflow-hidden ${className}`}
      style={{ background: fallback }}
    >
      {show && contain && (
        <img
          src={resolvedSrc}
          alt=""
          aria-hidden="true"
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-60 pointer-events-none"
        />
      )}
      {show && (
        <picture className="absolute inset-0 h-full w-full">
          {optimized?.avif && (
            <source
              type="image/avif"
              srcSet={optimized.avifSrcSet || optimized.avif}
              sizes={sizes || "(max-width: 640px) 100vw, (max-width: 1024px) 75vw, 1200px"}
            />
          )}
          {optimized?.webp && (
            <source
              type="image/webp"
              srcSet={optimized.webpSrcSet || optimized.webp}
              sizes={sizes || "(max-width: 640px) 100vw, (max-width: 1024px) 75vw, 1200px"}
            />
          )}
          <img
            src={resolvedSrc}
            srcSet={optimized?.webpSrcSet || undefined}
            alt={cleanAlt}
            width={width}
            height={height}
            sizes={sizes || "(max-width: 640px) 100vw, (max-width: 1024px) 75vw, 1200px"}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={eager ? "high" : "auto"}
            onLoad={handleLoad}
            onError={() => setFailed(true)}
            className={`absolute inset-0 h-full w-full ${contain ? "object-contain" : "object-cover"} ${imgClassName}`}
            style={{ objectPosition }}
          />
        </picture>
      )}
      {children}
    </div>
  );
}
